import { fetch as fetch$1 } from 'cross-fetch';
import '@rdfjs/data-model';
import '../rdf.internal.mjs';
import { getSourceIri, internal_defaultFetchOptions } from '../resource/resource.mjs';
import { fetch } from '../fetcher.mjs';
import 'n3';
import '../rdfjs.mjs';
import 'http-link-header';
import { getSolidDataset } from '../resource/solidDataset.mjs';
import { getThing, getThingAll, asIri } from '../thing/thing.mjs';
import { getIriAll } from '../thing/get.mjs';
import { foaf, pim } from '../constants.mjs';
import 'uuid';

//
/**
 * List all the alternative profiles IRI found in a given WebID profile.
 *
 * Note that some of these profiles may be private, and you may not have access to
 * the resulting resource.
 *
 * @param webId The WebID of the user's whose alternative profiles you are discovering.
 * @param webIdProfile The WebID profile obtained dereferencing the provided WebID.
 * @returns A list of URLs of the user's alternative profiles.
 * @since 1.20.0
 */
function getAltProfileUrlAllFrom(webId, webIdProfile) {
    const webIdThing = getThing(webIdProfile, webId);
    const altProfileUrlAll = getThingAll(webIdProfile)
        .filter((thing) => getIriAll(thing, foaf.primaryTopic).length > 0)
        .map(asIri)
        .concat(webIdThing ? getIriAll(webIdThing, foaf.isPrimaryTopicOf) : [])
        .filter((profileIri) => profileIri !== getSourceIri(webIdProfile));
    // Deduplicate the results.
    return Array.from(new Set(altProfileUrlAll));
}
async function getProfileAll(webId, options = internal_defaultFetchOptions) {
    var _a, _b;
    const authFetch = (_a = options.fetch) !== null && _a !== void 0 ? _a : fetch;
    const webIdProfile = (_b = options.webIdProfile) !== null && _b !== void 0 ? _b : (await getSolidDataset(webId, { fetch: fetch$1 }));
    const altProfileAll = (await Promise.allSettled(getAltProfileUrlAllFrom(webId, webIdProfile).map((uniqueProfileIri) => getSolidDataset(uniqueProfileIri, { fetch: authFetch }))))
        // Ignore the alternative profiles lookup which failed.
        .filter((result) => result.status === "fulfilled")
        .map((successfulResult) => successfulResult.value);
    return {
        webIdProfile,
        altProfileAll,
    };
}
/**
 * Discover the Pods an agent advertises for in their profile resources. Both the
 * agent's WebID and alternative profiles are fetched. Note that this function will
 * only return URLs of Pods linked to using the `pim:storage`, i.e. a triple
 * looking like <myWebid, pim:storage, myPodUrl> should appear in the profile
 * resources.
 *
 * @param webId The WebID of the agent whose Pods should be discovered
 * @param options Optional parameter
 * - `options.fetch`: An alternative `fetch` function to make the HTTP request,
 *    compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @returns a Promise resolving to an array containing the URLs of all the Pods
 * linked from the agent's profile resource using the `pim:storage` predicate.
 * @since 1.18.0
 */
async function getPodUrlAll(webId, options = internal_defaultFetchOptions) {
    const profiles = await getProfileAll(webId, options);
    return getPodUrlAllFrom(profiles, webId);
}
/**
 * Discover the Pods advertised for in the provided profile resources. Note that
 * this function will only return URLs of Pods linked to using the `pim:storage`
 * predicate, i.e. a triple looking like <myWebid, pim:storage, myPodUrl>
 * should appear in the profile resources.
 *
 * @param profiles The profile resources in which the Pods should be discovered
 * @param webId The WebID of the agent whose Pods should be discovered
 * @returns An array containing the URLs of all the Pods linked from the agent's
 * profile resource using the `pim:storage` predicate.
 * @since 1.18.0
 */
function getPodUrlAllFrom(profiles, webId) {
    const result = new Set();
    [profiles.webIdProfile, ...profiles.altProfileAll].forEach((profileResource) => {
        const webIdThing = getThing(profileResource, webId);
        if (webIdThing !== null) {
            getIriAll(webIdThing, pim.storage).forEach((podIri) => result.add(podIri));
        }
    });
    return Array.from(result);
}
/**
 * Get the WebID Profile document dataset.
 *
 * @example
 * ```
 * const webId = "https://example.org/people/me";
 * const profile = await getWebIdDataset(webId);
 * const podRoot = getPodUrlAllFrom({ webIdProfile: profile, altProfileAll: [] }, webId);
 * const profileThing = getThing(profile, webId);
 * const name = getStringNoLocale(profileThing, FOAF.name);
 * ```
 *
 * @param webId The WebID of the agent whose WebID Profile dataset is to be fetched.
 * @returns a SolidDataset for the WebID Profile document.
 * @since 1.24.0
 */
async function getWebIdDataset(webId) {
    return getSolidDataset(webId, { fetch: fetch$1 });
}

export { getAltProfileUrlAllFrom, getPodUrlAll, getPodUrlAllFrom, getProfileAll, getWebIdDataset };
